import React, { useState } from "react";
import { FoundBanner, FoundGrid, type Seat } from "../ranked/Found";
import { Radar } from "../ranked/Finding";
import RankInsignia from "../../shared/rating/RankInsignia";
import s from "./FfaFindingLab.module.scss";

// The 6 FFA search: six seats that fill as players are found, each empty seat
// its own "Finding enemy", the found ones showing who. Five sketches, desktop
// and phone, with a control for how many are in. Below, the match-found
// banner and grid at larger scales (the desktop ones read small). Linked from
// the Admin page.

const SEATS: Seat[] = [
  { id: "you", name: "You", colorId: "blue", flag: "se" },
  { id: "o1", name: "Kurvenkönig", colorId: "cyan", flag: "de" }, { id: "o2", name: "Zatacka_Zofia", colorId: "green", flag: "pl" },
  { id: "o3", name: "TightTurner", colorId: "yellow", flag: "gb" }, { id: "o4", name: "Slip_n_Slide", colorId: "purple", flag: "nl" }, { id: "o5", name: "InkTrail", colorId: "orange", flag: "cz" },
];
const COLORS: Record<string, string> = { blue: "#03A9F4", cyan: "#28c4d4", green: "#4CAF50", yellow: "#FFC107", purple: "#9C27B0", orange: "#FF9800" };
const RATINGS = [1480, 1320, null, 1610, null, 1275];

const SKETCHES = [
  { id: "1", name: "Seat tiles under", blurb: "Six tiles under the map (three by two): a radar in each empty seat, the flag, name and rank once someone is in. The only cards; the clock and Cancel are bare text." },
  { id: "2", name: "Roster rail", blurb: "A lobby roster beside the map on desktop (above it on the phone): six rows, the empty ones scanning. The most literal: it is the room filling up." },
  { id: "3", name: "The grid itself", blurb: "The search IS the starting grid, MS Battle's way: the six slots of the match-found grid over the map, each empty seat scanning until its player lands. When the sixth lands the grid slams to the banner." },
  { id: "4", name: "Six markers", blurb: "Six small seat markers in a row where the card was, a radar in each empty one, the flag and name once found. The lightest touch." },
  { id: "5", name: "Along the map", blurb: "Six seat badges along the map's bottom edge (three and three on the phone), the empty ones a radar. The map stays the hero." },
  { id: "6", name: "Two by three above", blurb: "Six tiles above the map, two columns by three rows, on desktop and phone alike: a radar in each empty seat, the flag, name and rank once found." },
  { id: "7", name: "Two by three, each side", blurb: "Desktop: the map, the two-by-three under it, the clock and Cancel under the rows. Phone: the mirror, the clock and Cancel on top, the rows, then the map." },
];

function Flag({ code }: { code?: string | null }) {
  return code ? <img className={s.flag} src={`https://flagcdn.com/w40/${code}.png`} alt="" /> : <span className={s.flagNone} />;
}
function Egg({ rating }: { rating: number | null }) {
  return rating != null ? <RankInsignia value={rating} size={22} /> : <RankInsignia rating={null} placeholder size={22} />;
}
function MiniFinding({ size = 34, label = true }: { size?: number; label?: boolean }) {
  return <span className={s.mini}><Radar size={size} />{label && <span className={s.miniTitle}>Finding enemy<i className={s.dots}><b /><b /><b /></i></span>}</span>;
}
function Clock() { return <span className={s.clock}>0:12</span>; }
// No card of its own: the clock and Cancel are bare text on the stage (the
// six seats are the only cards). At six, "Targets acquired!" takes the clock's
// place.
function Head({ found, phone }: { found: number; phone?: boolean }) {
  return (
    <div className={`${s.meta} ${phone ? s.metaPhone : ""}`}>
      {found === 6 ? <span className={s.acquired}>Targets acquired!</span> : <Clock />}
      <button type="button" className={s.cancelBare}>Cancel</button>
    </div>
  );
}
function Map({ phone, children }: { phone?: boolean; children?: React.ReactNode }) {
  return <div className={`${s.map} ${phone ? s.mapPhone : ""}`}><span className={s.chip}>Classic</span><span className={s.practice}>Practice round</span>{children}</div>;
}
function SeatTile({ seat, rating, found, compact }: { seat: Seat; rating: number | null; found: boolean; compact?: boolean }) {
  return (
    <div className={`${s.tile} ${found ? s.tileOn : ""} ${compact ? s.tileCompact : ""}`} style={{ "--worm": COLORS[seat.colorId] } as React.CSSProperties}>
      {found ? <><Flag code={seat.flag} /><span className={s.tileName}>{seat.name}</span><Egg rating={rating} /></> : <MiniFinding size={compact ? 26 : 32} label={!compact} />}
    </div>
  );
}

function Sketch({ id, found, phone }: { id: string; found: number; phone: boolean }) {
  const seats = SEATS.map((seat, i) => ({ seat, rating: RATINGS[i], found: i < found }));
  if (id === "1") return (
    <div className={s.col}><Map phone={phone} /><Head found={found} phone={phone} /><div className={`${s.tiles} ${phone ? s.tilesPhone : ""}`}>{seats.map((x) => <SeatTile key={x.seat.id} {...x} compact={phone} />)}</div></div>
  );
  if (id === "2") return (
    <div className={phone ? s.col : s.side}>
      {phone && <Head found={found} phone />}
      {phone && <div className={s.roster}>{seats.map((x) => <div key={x.seat.id} className={`${s.rowSeat} ${x.found ? s.rowOn : ""}`} style={{ "--worm": COLORS[x.seat.colorId] } as React.CSSProperties}>{x.found ? <><Flag code={x.seat.flag} /><span className={s.tileName}>{x.seat.name}</span><Egg rating={x.rating} /></> : <MiniFinding size={22} />}</div>)}</div>}
      <Map phone={phone} />
      {!phone && <div className={s.rail}><Head found={found} /><div className={s.roster}>{seats.map((x) => <div key={x.seat.id} className={`${s.rowSeat} ${x.found ? s.rowOn : ""}`} style={{ "--worm": COLORS[x.seat.colorId] } as React.CSSProperties}>{x.found ? <><Flag code={x.seat.flag} /><span className={s.tileName}>{x.seat.name}</span><Egg rating={x.rating} /></> : <MiniFinding size={24} />}</div>)}</div></div>}
    </div>
  );
  if (id === "3") return (
    <div className={s.col}>
      <Map phone={phone}>
        <div className={`${s.gridOver} ${phone ? s.gridOverPhone : ""}`}>
          {seats.map((x) => (
            <div key={x.seat.id} className={`${s.slot} ${x.found ? s.slotOn : ""}`} style={{ "--worm": COLORS[x.seat.colorId] } as React.CSSProperties}>
              {x.found ? <><Flag code={x.seat.flag} /><span className={s.tileName}>{x.seat.name}</span><Egg rating={x.rating} /></> : <MiniFinding size={phone ? 24 : 30} label={!phone} />}
            </div>
          ))}
        </div>
      </Map>
      <Head found={found} phone={phone} />
    </div>
  );
  if (id === "4") return (
    <div className={s.col}>
      <Map phone={phone} />
      <div className={`${s.markers} ${phone ? s.markersPhone : ""}`}>
        {seats.map((x) => (
          <span key={x.seat.id} className={`${s.marker} ${x.found ? s.markerOn : ""}`} style={{ "--worm": COLORS[x.seat.colorId] } as React.CSSProperties} title={x.found ? x.seat.name : "Finding enemy"}>
            {x.found ? <><Flag code={x.seat.flag} />{!phone && <span className={s.markerName}>{x.seat.name}</span>}</> : <Radar size={phone ? 22 : 28} />}
          </span>
        ))}
      </div>
      <Head found={found} phone={phone} />
    </div>
  );
  if (id === "7") return (
    <div className={s.colTop}>
      {phone && <Head found={found} phone />}
      {phone && <div className={`${s.tiles2} ${s.tiles2Phone}`}>{seats.map((x) => <SeatTile key={x.seat.id} {...x} compact />)}</div>}
      <Map phone={phone} />
      {!phone && <div className={s.tiles2}>{seats.map((x) => <SeatTile key={x.seat.id} {...x} />)}</div>}
      {!phone && <Head found={found} />}
    </div>
  );
  if (id === "6") return (
    <div className={s.colTop}>
      <div className={`${s.tiles2} ${phone ? s.tiles2Phone : ""}`}>{seats.map((x) => <SeatTile key={x.seat.id} {...x} compact={phone} />)}</div>
      <Head found={found} phone={phone} />
      <Map phone={phone} />
    </div>
  );
  return (
    <div className={s.col}>
      <Map phone={phone}>
        <div className={`${s.badges} ${phone ? s.badgesPhone : ""}`}>
          {seats.map((x) => <div key={x.seat.id} className={`${s.badge} ${x.found ? s.badgeOn : ""}`} style={{ "--worm": COLORS[x.seat.colorId] } as React.CSSProperties}>{x.found ? <><Flag code={x.seat.flag} /><span>{x.seat.name}</span></> : <Radar size={18} />}</div>)}
        </div>
      </Map>
      <Head found={found} phone={phone} />
    </div>
  );
}

export default function FfaFindingLab() {
  const [found, setFound] = useState(3);
  const [phone, setPhone] = useState(false);
  const [scale, setScale] = useState(1.25);
  const you = SEATS[0], opp = SEATS[1];
  return (
    <div className={s.lab}>
      <div className={s.bar}>
        <span className={s.barLabel}>Players found</span>
        <div className={s.seg}>{[0, 1, 2, 3, 4, 5, 6].map((n) => <button key={n} className={`${s.segBtn} ${n === found ? s.segOn : ""}`} onClick={() => setFound(n)}>{n}</button>)}</div>
        <span className={s.barLabel}>Device</span>
        <div className={s.seg}><button className={`${s.segBtn} ${!phone ? s.segOn : ""}`} onClick={() => setPhone(false)}>Desktop</button><button className={`${s.segBtn} ${phone ? s.segOn : ""}`} onClick={() => setPhone(true)}>Phone</button></div>
      </div>
      {SKETCHES.map((k) => (
        <section key={k.id} className={s.row}>
          <h2 className={s.h2}><b>{k.id}</b> {k.name} <span>{k.blurb}</span></h2>
          <div className={phone ? s.framePhone : s.frame}><Sketch id={k.id} found={found} phone={phone} /></div>
        </section>
      ))}
      <section className={s.row}>
        <h2 className={s.h2}><b>Scale</b> The banner and the grid, larger <span>The desktop ones read small at 1280 wide. The same components at a scale (desktop frame).</span></h2>
        <div className={s.bar}><span className={s.barLabel}>Scale</span><div className={s.seg}>{[1, 1.25, 1.5, 1.75].map((z) => <button key={z} className={`${s.segBtn} ${z === scale ? s.segOn : ""}`} onClick={() => setScale(z)}>{z}×</button>)}</div></div>
        <div className={s.stageFrame} style={{ zoom: scale } as React.CSSProperties}><div className={s.stageInner} style={{ width: `calc(1120px / ${scale})`, height: `calc(360px / ${scale})` }}><FoundBanner me={you} opp={opp} ratings={null} /></div></div>
        <div className={s.stageFrame} style={{ zoom: scale } as React.CSSProperties}><div className={s.stageInner} style={{ width: `calc(1120px / ${scale})`, height: `calc(420px / ${scale})` }}><FoundGrid seats={SEATS} youIds={["you"]} ratings={null} /></div></div>
      </section>
    </div>
  );
}
